import jsPDF from "jspdf";
import moment from "moment";

import { ComfortaaRegularFontBase64 } from "../../Helpers/ComfortaaRegularFontBase64";
import { ComfortaaBoldFontBase64 } from "../../Helpers/ComfortaaBoldFontBase64";
import { faviconPngBase64 } from "../../Helpers/faviconPngBase64";

type Maybe<T> = T | null | undefined;

type AccentTone = "default" | "success" | "danger" | "muted";

interface TableRow {
  label: string;
  value: Maybe<unknown>;
  tone?: AccentTone;
}

interface DematDetails {
  client_id: string;
  dp_id?: string;
  dp_name: string;
  dp_type: string;
  user_id: string;
  image_filename: string;
  is_deleted: boolean;
  inserted_at: string;
  updated_at: string;
  is_default: boolean;
  kyc_doc_plaintext_password: any;
  account_status: string;
  pan_numbers: string[];
  dan_status_check_response?: DanStatusCheckResponse;
  image: string;
  failed_count: number;
}

interface DanStatusCheckResponse {
  rrn?: number | string;
  dpId?: string;
  status?: string;
  statuscode?: string;
  errorcode?: string;
  timestamp?: string;
  errordescription?: string;
  clientId?: string;
  fstHoldrPan?: string;
  scndHoldrPan?: string;
  thrdHoldrPan?: string;
  transactionId?: string;
  reqseqno?: string;
}

interface TableBlock {
  title: string;
  rows: TableRow[];
}

export interface OfficeUsePdfPayload {
  user: any;
  panCard: any;
  proofOfAddressDetails?: any;
  bankDetails?: any[];
  dematDetails?: any;
  itrData?: {
    data?: {
      pan_status?: string;
      compliant?: boolean;
      pan_allotment_date?: string | Date;
      valid_pan?: boolean;
      pan_aadhaar_linked?: string;
      specified_person_under_206?: string;
    } | null;
  } | null;
}

const palette = {
  pageBackground: { r: 247, g: 247, b: 250 },
  title: { r: 33, g: 38, b: 50 },
  subtitle: { r: 123, g: 129, b: 151 },
  tableBorder: { r: 24, g: 30, b: 46 },
  tableHeaderFill: { r: 243, g: 244, b: 248 },
  success: { r: 72, g: 165, b: 82 },
  danger: { r: 214, g: 76, b: 85 },
  muted: { r: 151, g: 156, b: 170 },
};

const pageMetrics = {
  marginX: 56,
  top: 58,
  bottom: 62,
  tableSpacing: 20,
  tableHeaderHeight: 28,
  columnPaddingX: 16,
  columnPaddingY: 6,
  rowGap: 0,
};

const tableTextLineHeight = 10;

const nsdlStatusDescription: Record<string, string> = {
  "00": "Valid Record",
  "01": "DP ID does not match",
  "02": "Client ID does not match",
  "03": "DP ID, Client ID and PAN combination does not match",
  "04": "Account Status - Suspended",
  "05": "Account Status - Closed",
  "91": "Timeout",
};

const cdslStatusDescription: Record<string, string> = {
  "01": "Valid BO-PAN",
  "02": "Invalid BOID",
  "04": "BOID is closed",
  "05": "PAN not matching with the First Holder",
  "06": "PAN not matching with the Second Holder",
  "07": "PAN not matching with the Third Holder",
  "08": "Frozen for Credit",
  "09": "Frozen for Debit",
  "10": "Frozen for Both Debit And Credit",
  "-1": "Failure",
};

const successKeywords = [
  "yes",
  "verified",
  "linked",
  "valid record",
  "valid bo-pan",
  "operative",
  "compliant",
  "kra validated",
  "cams",
  "active",
];

const dangerKeywords = [
  "no",
  "rejected",
  "inactive",
  "not verified",
  "not linked",
];

const ensureString = (value: Maybe<unknown>): string => {
  if (value === null || value === undefined) {
    return "-";
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    const normalized = trimmed.toLowerCase();
    if (
      normalized === "null" ||
      normalized === "undefined" ||
      normalized === "nan" ||
      normalized === "none" ||
      normalized === "nil"
    ) {
      return "-";
    }
    return trimmed === "" ? "-" : trimmed;
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (typeof value === "number") {
    return Number.isNaN(value) ? "-" : String(value);
  }

  return String(value);
};

const normalizeToStringList = (...values: Maybe<unknown>[]): string[] => {
  const collected: string[] = [];

  values.forEach((value) => {
    if (value === null || value === undefined) {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item === null || item === undefined) {
          return;
        }
        collected.push(ensureString(item));
      });
      return;
    }

    collected.push(ensureString(value));
  });

  const cleaned = collected
    .map((item) => item.trim())
    .filter((item) => item && item !== "-");

  return Array.from(new Set(cleaned));
};

const formatList = (items: string[]): string =>
  items.length ? items.join(", ") : "-";

const formatDateGeneric = (
  value: Maybe<string | number | Date>,
  format: string,
  fallback = "-"
) => {
  if (!value) {
    return fallback;
  }

  if (typeof value === "string" && value.includes("(")) {
    return value;
  }

  const parsed = moment(value);
  if (!parsed.isValid()) {
    return ensureString(value);
  }
  return parsed.format(format);
};

const getToneColor = (doc: jsPDF, tone: AccentTone, text: string) => {
  if (tone === "success") {
    doc.setTextColor(palette.success.r, palette.success.g, palette.success.b);
    return;
  }
  if (tone === "danger") {
    doc.setTextColor(palette.danger.r, palette.danger.g, palette.danger.b);
    return;
  }
  if (tone === "muted") {
    doc.setTextColor(palette.muted.r, palette.muted.g, palette.muted.b);
    return;
  }

  const normalized = text.trim().toLowerCase();
  if (successKeywords.some((keyword) => normalized.includes(keyword))) {
    doc.setTextColor(palette.success.r, palette.success.g, palette.success.b);
    return;
  }
  if (dangerKeywords.some((keyword) => normalized === keyword)) {
    doc.setTextColor(palette.danger.r, palette.danger.g, palette.danger.b);
    return;
  }

  doc.setTextColor(palette.title.r, palette.title.g, palette.title.b);
};

const collectDefaultBank = (
  bankDetails: Maybe<any[]>
): Record<string, string> => {
  if (!bankDetails || !bankDetails.length) {
    return {
      "Verified Bank Account Name": "-",
      "Verified Bank Account Number": "-",
      "Penny Drop Verification Status": "-",
      "Penny Drop Audit Trail Stamp": "-",
    };
  }

  const resolved =
    bankDetails.find((item) => item?.is_default) || bankDetails[0] || {};

  return {
    "Verified Bank Account Name": ensureString(
      resolved?.beneficiary_name || resolved?.beneficiaryName
    ),
    "Verified Bank Account Number": ensureString(
      resolved?.account_number || resolved?.accountNumber
    ),
    "Penny Drop Verification Status": ensureString(
      resolved?.penny_drop_status ||
      resolved?.pennyDropStatus ||
      resolved?.verification_status ||
      "Verified"
    ),
    "Penny Drop Audit Trail Stamp": formatDateGeneric(
      resolved?.inserted_at,
      "YYYY-MM-DD, HH:mm:ss"
    ),
  };
};

const buildEmptyDematRows = () => [
  { label: "Sole or Joint Holder", value: "-" },
  { label: "Transaction ID", value: "-" },
  { label: "DP ID Client ID", value: "-" },
  { label: "First Holder PAN", value: "-" },
  { label: "Status Code", value: "-" },
  { label: "Status Description", value: "-" },
  { label: "Time Stamp", value: "-" },
  { label: "Second Holder PAN (if applicable)", value: "-" },
  { label: "Third Holder PAN (if applicable)", value: "-" },
];

const buildDematRows = (
  detail: Maybe<DematDetails>,
  registry: "NSDL" | "CDSL"
) => {
  if (!detail) {
    return buildEmptyDematRows();
  }

  const response = detail.dan_status_check_response;
  const isCDSL = registry === "CDSL";
  const dpId = ensureString(response?.dpId || detail.dp_id);
  const clientId = ensureString(response?.clientId || detail.client_id);
  const idParts = [dpId, clientId].filter((part) => part && part !== "-");
  const statusCodeRaw = ensureString(
    isCDSL ? response?.statuscode || response?.status : response?.status
  );
  const statusCode =
    statusCodeRaw === "-" || statusCodeRaw.startsWith("-")
      ? statusCodeRaw
      : isCDSL
        ? statusCodeRaw.padStart(2, "0")
        : statusCodeRaw;
  const statusDescription = ensureString(
    registry === "NSDL"
      ? nsdlStatusDescription[statusCode] || response?.errordescription
      : cdslStatusDescription[statusCode] ||
      response?.errordescription ||
      response?.status
  );
  const panNumbers = Array.isArray(detail.pan_numbers)
    ? detail.pan_numbers
    : [];
  const firstHolderPan = ensureString(response?.fstHoldrPan || panNumbers[0]);
  const secondHolderPan = ensureString(response?.scndHoldrPan || panNumbers[1]);
  const thirdHolderPan = ensureString(response?.thrdHoldrPan || panNumbers[2]);

  return [
    { label: "Account Status", value: ensureString(detail.account_status) },
    {
      label: "Transaction ID",
      value: ensureString(
        isCDSL
          ? response?.reqseqno || response?.transactionId
          : response?.transactionId
      ),
    },
    {
      label: "DP ID Client ID",
      value: idParts.length ? idParts.join(" ") : "-",
    },
    {
      label: "First Holder PAN",
      value: firstHolderPan,
      tone: "success",
    },
    { label: "Status Code", value: statusCode, tone: "muted" },
    { label: "Status Description", value: statusDescription, tone: "success" },
    {
      label: "Time Stamp",
      value: formatDateGeneric(detail.updated_at, "YYYY-MM-DD, HH:mm:ss"),
    },
    {
      label: "Second Holder PAN (if applicable)",
      value: secondHolderPan,
      tone: "success",
    },
    {
      label: "Third Holder PAN (if applicable)",
      value: thirdHolderPan,
      tone: "success",
    },
  ];
};

const drawPageChrome = (
  doc: jsPDF,
  uuidText: string,
  pageWidth: number,
  pageHeight: number
) => {
  doc.setFillColor(
    palette.pageBackground.r,
    palette.pageBackground.g,
    palette.pageBackground.b
  );
  doc.rect(0, 0, pageWidth, pageHeight, "F");

  doc.setFillColor(226, 229, 236);
  doc.rect(0, 0, pageWidth, 110, "F");

  doc.setFillColor(
    palette.pageBackground.r,
    palette.pageBackground.g,
    palette.pageBackground.b
  );

  doc.addImage(
    `data:image/jpeg;base64,${faviconPngBase64}`,
    "JPEG",
    pageMetrics.marginX,
    pageMetrics.top - 3,
    32,
    32
  );

  doc.setFont("Comfortaa", "bold");
  doc.setFontSize(18);
  doc.setTextColor(palette.title.r, palette.title.g, palette.title.b);
  doc.text(
    "SMEST Capital Audit Trail Report",
    pageMetrics.marginX + 46,
    pageMetrics.top + 20
  );

  doc.setFont("Comfortaa", "normal");
  doc.setFontSize(10.5);
  doc.setTextColor(palette.subtitle.r, palette.subtitle.g, palette.subtitle.b);
  doc.text(`UUID: ${uuidText}`, pageMetrics.marginX, pageMetrics.top + 44);

  return pageMetrics.top + 88;
};

const calculateTableHeight = (
  doc: jsPDF,
  table: TableBlock,
  pageWidth: number
) => {
  const tableWidth = pageWidth - pageMetrics.marginX * 2;
  const columnWidth = tableWidth / 2;
  let totalHeight = pageMetrics.tableHeaderHeight;

  doc.setFontSize(10);

  table.rows.forEach((row) => {
    const label = ensureString(row.label);
    const valueText = ensureString(row.value);

    const labelLines = doc.splitTextToSize(
      label,
      columnWidth - pageMetrics.columnPaddingX * 2
    );
    const valueLines = doc.splitTextToSize(
      valueText,
      columnWidth - pageMetrics.columnPaddingX * 2
    );

    const contentHeight = Math.max(
      pageMetrics.columnPaddingY * 2 + labelLines.length * tableTextLineHeight,
      pageMetrics.columnPaddingY * 2 + valueLines.length * tableTextLineHeight
    );

    totalHeight += contentHeight + pageMetrics.rowGap;
  });

  totalHeight += pageMetrics.tableSpacing;

  return totalHeight;
};

const drawTable = (
  doc: jsPDF,
  table: TableBlock,
  startY: number,
  pageWidth: number
) => {
  const tableWidth = pageWidth - pageMetrics.marginX * 2;
  const columnWidth = tableWidth / 2;
  const headerY = startY;

  doc.setDrawColor(
    palette.tableBorder.r,
    palette.tableBorder.g,
    palette.tableBorder.b
  );
  doc.setLineWidth(0.6);
  doc.setFillColor(
    palette.tableHeaderFill.r,
    palette.tableHeaderFill.g,
    palette.tableHeaderFill.b
  );

  doc.rect(
    pageMetrics.marginX,
    headerY,
    tableWidth,
    pageMetrics.tableHeaderHeight,
    "F"
  );
  doc.rect(
    pageMetrics.marginX,
    headerY,
    tableWidth,
    pageMetrics.tableHeaderHeight
  );

  doc.setFont("Comfortaa", "bold");
  doc.setFontSize(13);
  doc.setTextColor(palette.title.r, palette.title.g, palette.title.b);
  doc.text(
    table.title,
    pageMetrics.marginX + tableWidth / 2,
    headerY + pageMetrics.tableHeaderHeight / 2 + 4,
    { align: "center" }
  );

  doc.setFont("Comfortaa", "normal");

  let cursorY = headerY + pageMetrics.tableHeaderHeight;

  doc.setFontSize(10);

  table.rows.forEach((row, rowIndex) => {
    const label = ensureString(row.label);
    const valueText = ensureString(row.value);

    const labelLines = doc.splitTextToSize(
      label,
      columnWidth - pageMetrics.columnPaddingX * 2
    );
    const valueLines = doc.splitTextToSize(
      valueText,
      columnWidth - pageMetrics.columnPaddingX * 2
    );

    const contentHeight = Math.max(
      pageMetrics.columnPaddingY * 2 + labelLines.length * tableTextLineHeight,
      pageMetrics.columnPaddingY * 2 + valueLines.length * tableTextLineHeight
    );

    doc.setFillColor(255, 255, 255);
    doc.rect(pageMetrics.marginX, cursorY, tableWidth, contentHeight, "F");
    doc.rect(pageMetrics.marginX, cursorY, tableWidth, contentHeight);
    doc.line(
      pageMetrics.marginX + columnWidth,
      cursorY,
      pageMetrics.marginX + columnWidth,
      cursorY + contentHeight
    );

    const baseLabelY =
      cursorY + pageMetrics.columnPaddingY + tableTextLineHeight;
    doc.setTextColor(palette.title.r, palette.title.g, palette.title.b);
    labelLines.forEach((line, index) => {
      doc.text(
        line,
        pageMetrics.marginX + pageMetrics.columnPaddingX,
        baseLabelY + index * tableTextLineHeight
      );
    });

    const baseValueY =
      cursorY + pageMetrics.columnPaddingY + tableTextLineHeight;
    getToneColor(doc, row.tone ?? "default", valueText);
    valueLines.forEach((line, index) => {
      doc.text(
        line,
        pageMetrics.marginX + columnWidth + pageMetrics.columnPaddingX,
        baseValueY + index * tableTextLineHeight
      );
    });

    cursorY += contentHeight + pageMetrics.rowGap;

    if (rowIndex === table.rows.length - 1) {
      doc.setDrawColor(
        palette.tableBorder.r,
        palette.tableBorder.g,
        palette.tableBorder.b
      );
      doc.line(
        pageMetrics.marginX,
        cursorY,
        pageMetrics.marginX + tableWidth,
        cursorY
      );
    }

    doc.setTextColor(palette.title.r, palette.title.g, palette.title.b);
  });

  return cursorY + pageMetrics.tableSpacing;
};

export const generateOfficeUsePdf = (payload: OfficeUsePdfPayload) => {
  const {
    user,
    panCard,
    proofOfAddressDetails,
    bankDetails,
    dematDetails,
    itrData,
  } = payload;

  const doc = new jsPDF({
    unit: "pt",
    format: "a4",
    filters: ["ASCIIHexEncode"],
  });
  const pageWidth = doc.internal.pageSize.getWidth();
  let pageHeight = doc.internal.pageSize.getHeight();

  doc.addFileToVFS("Comfortaa-Regular.ttf", ComfortaaRegularFontBase64);
  doc.addFont("Comfortaa-Regular.ttf", "Comfortaa", "normal");
  doc.addFileToVFS("Comfortaa-Bold.ttf", ComfortaaBoldFontBase64);
  doc.addFont("Comfortaa-Bold.ttf", "Comfortaa", "bold");
  doc.setFont("Comfortaa", "normal");

  doc.setProperties({
    title: "SMEST User Audit Trail",
    subject: "SMEST Capital Audit Trail Report",
    author: "SMEST Admin",
  });

  const uuidText = ensureString(user?.uuid);
  let cursorY = drawPageChrome(doc, uuidText, pageWidth, pageHeight);

  const documentCreationTime = formatDateGeneric(
    (panCard?.document_creation_time ||
      panCard?.document_creation_timestamp ||
      panCard?.created_at ||
      panCard?.createdAt ||
      user?.created_at ||
      user?.createdAt ||
      panCard?.inserted_at ||
      new Date()) as Maybe<string | number | Date>,
    "DD-MM-YYYY HH:mm:ss"
  );

  const verificationSection: TableBlock = {
    title: "Verification Report",
    rows: [
      {
        label: "Client Name",
        value: `${ensureString(
          user?.first_name || user?.firstName
        )} ${ensureString(
          user?.last_name || user?.lastName
        )}`.replace(/^-\s-$/, "-"),
      },
      { label: "SMEST ID", value: user?.smestID || user?.smestId },
      {
        label: "Document Creation Time",
        value: documentCreationTime,
      },
    ],
  };

  const panResponse =
    panCard?.pan_status_response || panCard?.panStatusResponse || {};

  const kraStatusValues = normalizeToStringList(
    panResponse?.kyc_status_in_codes,
    panResponse?.kycStatusInCodes
  );
  const modificationRequestStatuses = normalizeToStringList(
    panResponse?.status_of_modification_request,
    panResponse?.statusOfModificationRequest
  );
  const modificationRejectRemarks = normalizeToStringList(
    panResponse?.modification_hold_reject_remarks,
    panResponse?.modificationHoldRejectRemarks
  );

  const kraSection: TableBlock = {
    title: "KRA Verification Report",
    rows: [
      {
        label: "Name as per KYC records",
        value: panResponse?.kyc_holder_name || panResponse?.kycHolderName,
      },
      {
        label: "PAN",
        value: panResponse?.pan_number || panResponse?.panNumber,
      },
      {
        label: "CVL KRA Status",
        value: formatList(kraStatusValues),
        tone: "success",
      },
      {
        label: "KRA Creation Date",
        value: formatDateGeneric(
          panResponse?.kyc_creation_date || panResponse?.kycCreationDate,
          "DD-MM-YYYY HH:mm:ss"
        ),
      },
      {
        label: "KRA Response Time",
        value: formatDateGeneric(
          panResponse?.response_date_time || panResponse?.responseDateTime,
          "DD-MM-YYYY HH:mm:ss"
        ),
      },
      {
        label: "eKYC Applicable",
        value: panResponse?.ekyc_is_applicable || panResponse?.ekycIsApplicable,
      },
      {
        label: "In Person Verification",
        value:
          panResponse?.in_person_verification ||
          panResponse?.inPersonVerification,
      },
      {
        label: "Hold or Deactivate Remarks",
        value: formatList(modificationRequestStatuses),
        tone: modificationRequestStatuses.length > 0 ? "success" : "muted",
      },
      {
        label: "Modification, if any, Reject Remarks",
        value: formatList(modificationRejectRemarks),
        tone: modificationRejectRemarks.length > 0 ? "success" : "muted",
      },
    ],
  };

  const incomeTaxSection: TableBlock = {
    title: "Income Tax Database Verification Report",
    rows: [
      {
        label: "PAN Number Compliant",
        value: itrData?.data?.compliant,
        tone: "success",
      },
      {
        label: "PAN Allotment Date",
        value: formatDateGeneric(
          itrData?.data?.pan_allotment_date,
          "YYYY-MM-DD"
        ),
        tone: "default",
      },
      {
        label: "PAN Status",
        value: itrData?.data?.pan_status,
      },
      {
        label: "Valid PAN",
        value: itrData?.data?.valid_pan,
      },
      {
        label: "Aadhar PAN Seeding Status",
        value: itrData?.data?.pan_aadhaar_linked,
      },
      {
        label: "Specified Person under 206",
        value: itrData?.data?.specified_person_under_206,
        tone: "success",
      },
    ],
  };

  const firstPageSections = [verificationSection, kraSection, incomeTaxSection];

  firstPageSections.forEach((section) => {
    const tableHeight = calculateTableHeight(doc, section, pageWidth);
    if (cursorY + tableHeight > pageHeight - pageMetrics.bottom) {
      doc.addPage();
      pageHeight = doc.internal.pageSize.getHeight();
      cursorY = drawPageChrome(doc, uuidText, pageWidth, pageHeight);
    }
    cursorY = drawTable(doc, section, cursorY, pageWidth);
  });

  const bankSnapshot = collectDefaultBank(bankDetails);
  const dematArray: DematDetails[] = Array.isArray(dematDetails)
    ? dematDetails
    : [];
  const defaultDemat =
    dematArray.find((detail) => detail?.is_default) || dematArray[0] || null;
  const dematType = defaultDemat?.dp_type?.toUpperCase();

  const nsdlRows = buildDematRows(
    dematType === "NSDL" ? defaultDemat : null,
    "NSDL"
  );
  const cdslRows = buildDematRows(
    dematType === "CDSL" ? defaultDemat : null,
    "CDSL"
  );

  const bankSection: TableBlock = {
    title: "Bank Verification Report via Penny Drop Mechanism",
    rows: Object.keys(bankSnapshot).map((label) => ({
      label,
      value: bankSnapshot[label],
    })),
  };

  const nsdlSection: TableBlock = {
    title: "NSDL DMat Verification Report",
    rows: nsdlRows,
  };

  const cdslSection: TableBlock = {
    title: "CDSL DMat Verification Report",
    rows: cdslRows,
  };

  doc.addPage();
  pageHeight = doc.internal.pageSize.getHeight();
  cursorY = drawPageChrome(doc, uuidText, pageWidth, pageHeight);

  const secondPageSections = [bankSection, nsdlSection, cdslSection];

  secondPageSections.forEach((section) => {
    const tableHeight = calculateTableHeight(doc, section, pageWidth);
    if (cursorY + tableHeight > pageHeight - pageMetrics.bottom) {
      doc.addPage();
      pageHeight = doc.internal.pageSize.getHeight();
      cursorY = drawPageChrome(doc, uuidText, pageWidth, pageHeight);
    }
    cursorY = drawTable(doc, section, cursorY, pageWidth);
  });

  doc.setFontSize(9);
  doc.setTextColor(palette.muted.r, palette.muted.g, palette.muted.b);
  doc.text(
    "Confidential: Generated via SMEST Admin for internal audit reference only.",
    pageMetrics.marginX,
    pageHeight - pageMetrics.marginX / 2
  );

  const firstName = ensureString(user?.firstName).replace(/\s+/g, "");
  const lastName = ensureString(user?.lastName).replace(/\s+/g, "");
  const safeName = `${firstName}_${lastName}`.replace(/[^A-Za-z0-9_]+/g, "");
  const fileName = safeName.trim() ? safeName : "smest_user";

  doc.save(`${fileName}_audit_trail.pdf`);
};
